/* eslint-disable */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import { OpenAlertBadge, StatusBadge } from "@/components/shared/StatusBadge";
import { formatCancerDisplay, formatTreatmentDisplay } from "@/components/shared/OncologyContext";
import api from "@/api";
import { toast } from "sonner";
import {
  Users, Search, RefreshCw, ChevronRight, Activity, Filter, UserPlus
} from "lucide-react";
import { getItems } from "@/utils/apiHelpers";
import { CreatePatientModal } from "@/components/admin/modals/ClinicAdminModals";

export default function DoctorPatients() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, critical, to_monitor
  const [showCreate, setShowCreate] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [secretaries, setSecretaries] = useState([]);

  const fetchMembers = async () => {
    try {
      const res = await api.get("/users/clinic-members").catch(() => ({ data: { items: [] } }));
      const list = getItems(res.data);
      setDoctors(list.filter(u => u.role === "MEDECIN"));
      setNurses(list.filter(u => u.role === "IDE"));
      setSecretaries(list.filter(u => u.role === "SECRETAIRE_MEDICAL"));
    } catch {}
  };

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const res = await api.get("/patients?limit=500");
      const patientsList = getItems(res.data);
      const sorted = patientsList.sort((a, b) => {
        const order = { CRITICAL: 0, OPEN_ALERT: 1, TO_MONITOR: 2, STABLE: 3, HOSPITALIZED: 4 };
        const orderA = order[a.status] ?? 4;
        const orderB = order[b.status] ?? 4;
        if (orderA !== orderB) return orderA - orderB;
        // Secondary sort by name
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
      });
      setPatients(sorted);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
    fetchMembers();
  }, []);

  // Filter patients
  const filteredPatients = patients.filter(p => {
    // Search filter
    const matchesSearch = 
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      p.pathology?.toLowerCase().includes(search.toLowerCase());
    
    // Status filter
    if (filter === "critical") return matchesSearch && p.status === "CRITICAL";
    if (filter === "to_monitor") return matchesSearch && (["CRITICAL", "OPEN_ALERT", "TO_MONITOR"].includes(p.status));
    
    return matchesSearch;
  });

  return (
    <ProfessionalLayout>
      <div className="space-y-4 lg:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2 lg:gap-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Users className="w-6 h-6 lg:w-7 lg:h-7 text-emerald-600" />
              Mes patients
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {filteredPatients.length} patient{filteredPatients.length > 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="doctor-create-patient-btn"
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors min-h-[44px]"
            >
              <UserPlus className="w-4 h-4" /> <span className="hidden sm:inline">Créer un patient</span><span className="sm:hidden">Créer</span>
            </button>
          <button
            onClick={fetchPatients}
            data-testid="doctor-refresh-patients-button"
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg min-h-[44px]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
          </div>
        </div>

        {/* Search and filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un patient..."
              data-testid="doctor-patient-search-input"
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none min-h-[44px]"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1">
            {[
              { value: "all", label: "Tous" },
              { value: "critical", label: "Critiques" },
              { value: "to_monitor", label: "À surveiller" },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`doctor-patient-filter-${f.value}`}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors flex-shrink-0 min-h-[44px] ${
                  filter === f.value
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Patients list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Aucun patient trouvé</p>
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="lg:hidden space-y-2">
              {filteredPatients.map(patient => (
                <Link
                  key={patient.id}
                  to={`/doctor/patients/${patient.id}`}
                  data-testid={`doctor-patient-card-${patient.id}`}
                  className="dash-card block p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold flex-shrink-0">
                      {patient.firstName?.[0]}{patient.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-slate-800 truncate">{patient.firstName} {patient.lastName}</p>
                        <div className="flex flex-wrap justify-end gap-1.5" data-testid={`doctor-patient-card-status-${patient.id}`}>
                          <StatusBadge status={patient.currentStatus || patient.status || "STABLE"} testId={`doctor-patient-card-current-status-${patient.id}`} />
                          <OpenAlertBadge count={patient.openAlertsCount} level={patient.highestOpenAlertLevel} testId={`doctor-patient-card-open-alerts-${patient.id}`} />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{patient.phone}</p>
                      <p className="text-sm text-slate-700 truncate mt-1">{patient.pathology}</p>
                      <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                        <span className="truncate">{patient.treatment || "—"}</span>
                        <span className="flex-shrink-0">
                          {patient.lastCheckinAt
                            ? new Date(patient.lastCheckinAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
                            : "Aucun CI"}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                  </div>
                </Link>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="dash-card p-0 overflow-hidden hidden lg:block">
              <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Patient</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Pathologie</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Traitement</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Statut actuel</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Alertes ouvertes</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Dernier check-in</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPatients.map(patient => (
                    <tr key={patient.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold">
                            {patient.firstName?.[0]}{patient.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{patient.firstName} {patient.lastName}</p>
                            <p className="text-xs text-slate-500">{patient.phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-slate-700">{formatCancerDisplay(patient)}</p>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {formatTreatmentDisplay(patient)}
                        </span>
                      </td>
                      <td className="p-4">
                        <StatusBadge status={patient.currentStatus || patient.status || "STABLE"} testId={`doctor-patient-table-current-status-${patient.id}`} />
                      </td>
                      <td className="p-4" data-testid={`doctor-patient-open-alerts-${patient.id}`}>
                        <OpenAlertBadge count={patient.openAlertsCount} level={patient.highestOpenAlertLevel} testId={`doctor-patient-table-open-alerts-${patient.id}`} />
                        {!patient.openAlertsCount && <span className="text-xs text-slate-400">Aucune</span>}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-slate-500">
                          {patient.lastCheckinAt
                            ? new Date(patient.lastCheckinAt).toLocaleDateString("fr-FR", {
                                day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
                              })
                            : "Aucun"
                          }
                        </span>
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/doctor/patients/${patient.id}`}
                          data-testid={`doctor-patient-view-link-${patient.id}`}
                          className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                        >
                          Voir <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}
      </div>

      {showCreate && (
        <CreatePatientModal
          doctors={doctors}
          nurses={nurses}
          secretaries={secretaries}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchPatients(); }}
        />
      )}
    </ProfessionalLayout>
  );
}
